import { QueryClient } from '@tanstack/react-query';

/**
 * TanStack Query configuration optimized for our video app
 * 
 * Key performance settings:
 * - 5 minute stale time: Data stays fresh for 5 minutes before refetching
 * - 10 minute cache time: Data stays in memory for 10 minutes after components unmount
 * - Exponential backoff retries: 1s → 2s → 4s → 8s delays
 * - Background refetching when window refocuses
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache Strategy
      staleTime: 5 * 60 * 1000, // 5 minutes - how long data is considered fresh
      gcTime: 10 * 60 * 1000,   // 10 minutes - how long data stays in cache (was 'cacheTime' in v4)
      
      // Retry Strategy with exponential backoff
      retry: (failureCount, error: any) => {
        // Don't retry on auth errors (401) or not found (404)
        if (error?.status === 401 || error?.status === 404) {
          // Invalidate all queries on auth errors to force fresh data
          queryClient.invalidateQueries();
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // 1s, 2s, 4s, max 30s
      
      // Background Refetching
      refetchOnWindowFocus: true,  // Refetch when user returns to tab
      refetchOnMount: true,        // Refetch when component mounts
      refetchOnReconnect: true,    // Refetch when internet reconnects
      
      // Performance Settings
      networkMode: 'online',       // Only run queries when online
    },
    mutations: {
      // Mutation Retry Strategy
      retry: (failureCount, error: any) => {
        // Don't retry mutations on client errors (4xx)
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        // Retry server errors (5xx) up to 2 times
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // 1s, 2s, max 10s
      
      // Performance
      networkMode: 'online',
    },
  },
});

/**
 * Query keys factory for consistent cache key management
 * 
 * This pattern ensures:
 * - Consistent naming across the app
 * - Easy cache invalidation
 * - Type safety for query keys
 */
export const queryKeys = {
  // Quotes
  quotes: {
    all: ['quotes'] as const,
    filtered: (filter: string, stream: string) => ['quotes', 'filtered', filter, stream] as const,
    byStatus: (status: string) => ['quotes', 'status', status] as const,
    detail: (id: string) => ['quotes', 'detail', id] as const,
  },
  
  // Streams
  streams: {
    all: ['streams'] as const,
    detail: (id: string) => ['streams', 'detail', id] as const,
  },
  
  // User data
  user: {
    credits: ['user', 'credits'] as const,
    profile: ['user', 'profile'] as const,
  },
  
  // Jobs/ECS
  jobs: {
    active: ['jobs', 'active'] as const,
    count: ['jobs', 'count'] as const,
  },

  // Stories
  stories: {
    all: ['stories'] as const,
    list: (streamId?: string, status?: string) => ['stories', 'list', streamId, status] as const,
    detail: (id: string) => ['stories', 'detail', id] as const,
  },

// NOTE: Series query keys removed - Series functionality deprecated

  // Voices (cached for 5 min - language-first loading)
  voices: {
    all: ['voices'] as const,
    languages: ['voices', 'languages'] as const,
    byLanguage: (language: string) => ['voices', 'byLanguage', language] as const,
  },

  // Characters (PuLID face consistency)
  characters: {
    all: ['characters'] as const,
    byUser: (userId: string) => ['characters', 'user', userId] as const,
    byStream: (streamId: string) => ['characters', 'stream', streamId] as const,
    detail: (id: string) => ['characters', 'detail', id] as const,
  },

  // Engagement (Comment Intelligence - Issue #1326)
  engagement: {
    all: ['engagement'] as const,
    comments: (channelId: string, filters?: Record<string, any>) =>
      ['engagement', 'comments', channelId, filters] as const,
    stats: (channelId: string) => ['engagement', 'stats', channelId] as const,
  },

  // Analytics Dashboard (Phase 2a)
  analytics: {
    all: ['analytics'] as const,
    dashboard: (channelId: string, days: number) =>
      ['analytics', 'dashboard', channelId, days] as const,
  },

  // Storyboard Interview (Issue #1982)
  interview: {
    all: ['interview'] as const,
    configs: ['interview', 'configs'] as const,
  },

  // VIE — Viral Intelligence Engine (Issue #1807)
  vie: {
    all: ['vie'] as const,
    dashboard: ['vie', 'dashboard'] as const,
    techniques: (params?: { limit?: number; category?: string }) =>
      ['vie', 'techniques', params] as const,
    recommendations: ['vie', 'recommendations'] as const,
    replies: (videoId?: string) => ['vie', 'replies', videoId] as const,
  },
} as const;

/**
 * Helper function to invalidate related queries
 * 
 * Example usage after quote approval:
 * invalidateQueries(['quotes']) // Invalidates all quote queries
 */
export const invalidateQuoteQueries = () => {
  queryClient.invalidateQueries({ queryKey: queryKeys.quotes.all });
};

export const invalidateStreamQueries = () => {
  queryClient.invalidateQueries({ queryKey: queryKeys.streams.all });
};